<template>
  <v-container class="new-campaign-devices-and-services">
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      top
      :color="snackbar.color"
      rounded
      centered
    >
      {{ snackbar.text }}
    </v-snackbar>

    <!-- Warning dialog for invalid start dates -->
    <v-dialog
      v-if="warningDialog.show"
      v-model="warningDialog.show"
      width="auto"
    >
      <div class="start-date-warning-dialog">
        <v-card color="#EEF2F5" class="py-4 px-4" rounded="lg">
          <v-card-title class="justify-center pt-4">
            <v-img :src="require('@/assets/icons/alert.png')" max-width="1.5rem" max-height="1.5rem" />
            <p class="title-text ml-2 mb-0">{{ $t('NewCampaignInitialData.devicesAndServices.startDateWarningTitle') }}</p>
          </v-card-title>
          <v-card-text class="body-text text-center px-6">
            <p class="mb-8">{{ $t('NewCampaignInitialData.devicesAndServices.startDateWarningMessageFirst') }}</p>
            <p>{{ $t('NewCampaignInitialData.devicesAndServices.startDateWarningMessageSecond') }}</p>
          </v-card-text>

          <v-card-actions>
            <v-row>
              <v-col align="center">
                <v-btn
                  class="confirm-button"
                  color="#0098E1"
                  medium
                  @click="closeWarningDialog"
                >
                  {{ $t('Config.ok') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </div>
    </v-dialog>

    <v-dialog
      v-if="newDeviceDialog"
      v-model="newDeviceDialog"
      width="500"
      content-class="new-device-dialog"
    >
      <v-card>
        <v-card-title tag="p" class="new-device-dialog-title text-break justify-center mb-2">
          {{$t('NewCampaignInitialData.devicesAndServices.setNewDeviceTitle')}}
        </v-card-title>

        <v-card-text>
          <v-row justify="center">
            <v-col cols="12" md="8">
              <v-text-field
                v-model="newDeviceName"
                :label="`${$t('NewCampaignInitialData.devicesAndServices.setNewDeviceLabel')}`"
                :title="$t('NewCampaignInitialData.devicesAndServices.setNewDeviceLabel')"
                background-color="white"
                outlined
                persistent-hint
                flat
                :rules="[rules.fieldRequired]"
                hide-details="auto"
              />
            </v-col>
          </v-row>
          <v-row justify="center" class="mt-2">
            <v-btn :loading="loadingNewDevice" :disabled="!newDeviceName" color="#0098E1" class="submit-button" @click="createNewDevice()">{{$t('NewCampaignInitialData.devicesAndServices.setNewDeviceSubmitButton')}}</v-btn>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <AddUserDialog
      v-if="addUserDialog"
      :dialog.sync="addUserDialog"
      :fromNewCampaign="true"
      :campaignId="campaignId"
    />
    <p class="title-text mt-2">{{$t('NewCampaignInitialData.devicesAndServices.title')}}</p>
    <p class="body-text text-center mt-2">{{$t('NewCampaignInitialData.devicesAndServices.subtitle')}}</p>
    <v-row justify="center" align="center" class="mt-2">
      <v-img :src="require('@/assets/icons/lightbulb.png')" max-width="1.25rem" max-height="1.25rem" />
      <p class="body-text text-center font-italic ml-2 mb-0">{{$t('NewCampaignInitialData.devicesAndServices.tip')}}</p>
      <v-btn :loading="loadingInviteUsers" color="#0098E1" small class="invite-users-button ml-4" @click="inviteUsers()">{{$t('NewCampaignInitialData.devicesAndServices.inviteUsers')}}</v-btn>
    </v-row>

    <p class="section-text text-left pt-4">{{$t('NewCampaignInitialData.devicesAndServices.contractedEquipmentsTitle')}}</p>
    <v-form v-model="isFormValid" ref="productsForm" class="action-row">
      <div>
        <!-- Monitoring products -->
        <template v-for="(product, index) in monitoringProducts">
          <v-row align="baseline">
            <p class="body-text pointer" @click="toggleProductExpanded('monitoring', index)"><b>{{product.quantity}}</b> {{getProductDetailText(product)}}</p>
            <v-btn class="ml-2" text fab x-small @click="toggleProductExpanded('monitoring', index)">
              <v-icon
                color="black"
              >{{monitoringProductsExpanded[index] ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon>
            </v-btn>
          </v-row>
          <div v-show="monitoringProductsExpanded[index]" v-for="(deviceInfo, deviceIndex) in monitoringProductsData[index].devices">
            <v-row>
              <v-col cols="12" md="2">
                <v-autocomplete
                  :menu-props="{ offsetY: true }"
                  :items="newDevicesItemsList[`${index}-${deviceIndex}`]
                    ? [{id: `${index}-${deviceIndex}`, campaign: {entityName: newDevicesItemsList[`${index}-${deviceIndex}`].name}}, ...getDevices(deviceInfo.device, product.deviceType)]
                    : getDevices(deviceInfo.device, product.deviceType)"
                  item-text="campaign.entityName"
                  item-value="id"
                  v-model="deviceInfo.device"
                  :label="`${$t('NewCampaignInitialData.devicesAndServices.device')} *`"
                  :title="$t('NewCampaignInitialData.devicesAndServices.device')"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="[rules.fieldRequired]"
                  hide-details="auto"
                  @change="handleSelectDevice(index, deviceIndex)"
                  :no-data-text="$t('NewCampaignInitialData.devicesAndServices.noDevices')"
                >
                  <template v-slot:prepend-item>
                    <v-list-item @click="addNewDevice(index, deviceIndex)">
                      <v-list-item-title class="add-field-btn">
                        + {{ $t('NewCampaignInitialData.devicesAndServices.addNewDevice') }}
                      </v-list-item-title>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" md="2">
                <v-menu
                  v-model="deviceInfo.startDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :key="`startDate-${index}`"
                      v-model="deviceInfo.startDate"
                      :label="`${$t('NewCampaignInitialData.devicesAndServices.startDate')} *`"
                      :title="$t('NewCampaignInitialData.devicesAndServices.startDate')"
                      background-color="white"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      persistent-hint
                      flat
                      color="#0098E1"
                      clearable
                      :rules="[rules.fieldRequired, rules.validateStartDate]"
                      hide-details="auto"
                      append-icon="mdi-calendar"
                      @click:append="deviceInfo.startDateMenu = true"
                    />
                  </template>
                  <v-date-picker
                    v-model="deviceInfo.auxStartDate"
                    @change="handleDeviceDateChange($event, deviceInfo, 'startDate')"
                    color="#0098E1"
                    locale="es-AR"
                    :min="getCurrentDate()"
                  />
                </v-menu>
              </v-col>
              <v-col cols="12" md="2">
                <v-menu
                  v-model="deviceInfo.endDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :key="`endDate-${index}`"
                      v-model="deviceInfo.endDate"
                      :label="`${$t('NewCampaignInitialData.devicesAndServices.endDate')} *`"
                      :title="$t('NewCampaignInitialData.devicesAndServices.endDate')"
                      background-color="white"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      persistent-hint
                      flat
                      color="#0098E1"
                      clearable
                      :rules="[rules.fieldRequired, (v) => validateEndDateForDevice(v, deviceInfo, monitoringProductsData[index])]"
                      :disabled="!deviceInfo.startDate || deviceHasExtension(index, deviceIndex)"
                      hide-details="auto"
                      append-icon="mdi-calendar"
                      @click:append="deviceInfo.endDateMenu = true"
                    />
                  </template>
                  <v-date-picker
                    v-model="deviceInfo.auxEndDate"
                    @change="handleDeviceDateChange($event, deviceInfo, 'endDate')"
                    color="#0098E1"
                    locale="es-AR"
                    :min="getCurrentDate()"
                  />
                </v-menu>
              </v-col>
              <v-col cols="12" md="2">
                <v-autocomplete
                  :menu-props="{ offsetY: true }"
                  :items="fields"
                  item-text="name"
                  item-value="id"
                  v-model="deviceInfo.field"
                  :label="`${$t('NewCampaignInitialData.devicesAndServices.field')} *`"
                  :title="$t('NewCampaignInitialData.devicesAndServices.field')"
                  outlined
                  background-color="#FFF"
                  color="#0098E1"
                  :rules="[rules.fieldRequired]"
                  hide-details="auto"
                  :no-data-text="$t('NewCampaignInitialData.devicesAndServices.noFields')"
                  @change="handleDataChange"
                >
                  <template v-slot:prepend-item>
                    <v-list-item @click="addNewField(index, deviceIndex)">
                      <v-list-item-title class="add-field-btn">
                        + {{ $t('NewCampaignInitialData.devicesAndServices.addNewField') }}
                      </v-list-item-title>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="deviceInfo.coordinates"
                  :label="`${$t('NewCampaignInitialData.devicesAndServices.coordinates')} *`"
                  :title="$t('NewCampaignInitialData.devicesAndServices.coordinates')"
                  :hint="$t('NewCampaignInitialData.devicesAndServices.coordinatesHint')"
                  background-color="white"
                  outlined
                  persistent-hint
                  flat
                  :rules="[rules.fieldRequired, rules.validateLatLon]"
                  hide-details="auto"
                  @blur="handleDataChange"
                  validate-on-blur
                />
              </v-col>
            </v-row>
            <v-divider v-if="deviceIndex < monitoringProductsData[index].devices.length - 1" class="mt-2 mb-6" />
          </div>
        </template>

        <!-- Extensions products -->
        <template v-for="(product, index) in extensionProducts">
          <v-row align="baseline">
            <p class="body-text pointer" @click="toggleProductExpanded('extension', index)"><b>{{product.quantity}}</b> {{getProductDetailText(product)}}</p>
            <v-btn class="ml-2" text fab x-small @click="toggleProductExpanded('extension', index)">
              <v-icon
                color="black"
              >{{extensionProductsExpanded[index] ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon>
            </v-btn>
          </v-row>
          <div v-show="extensionProductsExpanded[index]">
            <v-row align="baseline">
              <p class="extension-description-text">{{$t('NewCampaignInitialData.devicesAndServices.extensionProductsDescription')}}</p>
            </v-row>
            <div v-for="(extensionInfo, extensionIndex) in extensionProductsData[index].extensions">
              <v-divider v-if="extensionIndex > 0" class="my-2" />
              <v-row>
                <v-col cols="12" md="2">
                  <v-autocomplete
                    :menu-props="{ offsetY: true }"
                    :items="getExtensionDevices(extensionInfo.device)"
                    item-text="campaign.entityName"
                    item-value="id"
                    v-model="extensionInfo.device"
                    :label="`${$t('NewCampaignInitialData.devicesAndServices.device')} *`"
                    :title="$t('NewCampaignInitialData.devicesAndServices.device')"
                    outlined
                    background-color="#FFF"
                    color="#0098E1"
                    hide-details="auto"
                    :no-data-text="$t('NewCampaignInitialData.devicesAndServices.noDevices')"
                    @change="handleDataChange"
                    :rules="[() => validateExtensionDevice(extensionInfo)]"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    :menu-props="{ offsetY: true }"
                    :items="[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]"
                    v-model="extensionInfo.unitsToExtend"
                    :label="`${$t('NewCampaignInitialData.devicesAndServices.unitsToExtend')}`"
                    :title="$t('NewCampaignInitialData.devicesAndServices.unitsToExtend')"
                    outlined
                    background-color="#FFF"
                    color="#0098E1"
                    hide-details="auto"
                    :rules="[() => validateUnitsToExtend(index)]"
                    @change="handleDataChange"
                  />
                </v-col>
                <v-col cols="12" md="2">
                  <v-menu
                    v-model="extensionInfo.endDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :key="`endDate-${extensionIndex}`"
                        v-model="extensionInfo.endDate"
                        :label="`${$t('NewCampaignInitialData.devicesAndServices.endDate')}`"
                        :title="$t('NewCampaignInitialData.devicesAndServices.endDate')"
                        background-color="white"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        persistent-hint
                        flat
                        color="#0098E1"
                        clearable
                        hide-details="auto"
                        append-icon="mdi-calendar"
                        @click:append="extensionInfo.endDateMenu = true"
                        :disabled="!extensionInfo.device"
                        :rules="[() => validateEndDateForExtension(extensionInfo.endDate, extensionInfo, extensionProductsData[index])]"
                      />
                    </template>
                    <v-date-picker
                      v-model="extensionInfo.auxEndDate"
                      @change="handleExtensionDateChange($event, extensionInfo, 'endDate')"
                      color="#0098E1"
                      locale="es-AR"
                      :min="getCurrentDate()"
                    />
                  </v-menu>
                </v-col>
                <v-col align-self="center">
                  <a @click="removeExtension(index, extensionIndex)" class="remove-extension-btn">{{$t('NewCampaignInitialData.devicesAndServices.removeExtension')}}</a>
                </v-col>
              </v-row>
            </div>
            <v-row class="mt-1" align="baseline" v-if="hasUnassignedExtensions(index)">
              <a @click="addNewExtension(index)" class="add-extension-btn">{{$t('NewCampaignInitialData.devicesAndServices.addNewExtension')}}</a>
            </v-row>
          </div>
        </template>
      </div>

      <v-row class="mt-2" align="end">
        <v-col align="end">
          <v-btn color="#FFFFF" class="back-button" @click="goBack()">{{$t('NewCampaignInitialData.devicesAndServices.back')}}</v-btn>
        </v-col>
        <v-col align="start">
          <v-btn :loading="loading" color="#0098E1" class="submit-button" @click="finish()">{{$t('NewCampaignInitialData.devicesAndServices.finish')}}</v-btn>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import { EQUIPMENT_TYPE, CAMPAIGN_VALIDATION, FIRESTORE_CAMPAIGN_PRE_DATA_STEPS, CAMPAIGN_STEPS, WEBHOOKS_PATHS, EVENTS_TO_TRACK, PRODUCT_TYPES } from '../../constants/const'
import { validateLatLon } from '../../lib/helpers'
import _sortBy from 'lodash/sortBy'
import _get from 'lodash/get'
import _cloneDeep from 'lodash/cloneDeep'
import { updatePreDataInformation, completePreDataStep } from '../../lib/crudOperations/write'
import AddUserDialog from '../Additions/AddUser/AddUserDialog.vue'
import { auth } from '../../plugins/firebase'
import { trackEvent } from '../../lib/track-events'

export default {
  name: 'NewCampaignDevicesAndServices',
  components: {
    AddUserDialog
  },
  props: {
    nextStep: {
      type: Function,
      required: true
    },
    previousStep: {
      type: Function,
      required: true
    },
    products: {
      type: Array,
      required: true
    },
    campaignId: {
      type: String,
      required: true
    },
    comesFromAnotherRoute: {
      type: Boolean,
      required: false
    },
    comesFromAnotherRouteData: {
      type: Object,
      required: false
    },
    saveMethods: {
      type: Object,
      required: false
    }
  },
  data () {
    return {
      monitoringProductsExpanded: [],
      extensionProductsExpanded: [],
      monitoringProductsData: [],
      extensionProductsData: [],
      oldMonitoringProductsData: [],
      oldExtensionProductsData: [],
      rules: {
        fieldRequired: v => !!v || this.$t('Config.fieldIsRequired'),
        arrayFieldRequired: v => !!v && !!v.length || this.$t('Config.fieldIsRequired'),
        validateLatLon: v => validateLatLon(v) || this.$t('Config.allCoordinatesRule'),
        validateStartDate: (startDate) => {
          if (!startDate) return true // If no date, don't validate here (already validated with fieldRequired)
          const [day, month, year] = startDate.split('/')
          const selectedDate = new Date(year, month - 1, day)
          const minDate = new Date()
          minDate.setDate(minDate.getDate() + CAMPAIGN_VALIDATION.MIN_DAYS_BEFORE_START)
          return selectedDate >= minDate || this.$t('Config.startDateMinDaysRule', {minDays: CAMPAIGN_VALIDATION.MIN_DAYS_BEFORE_START})
        }
      },
      isFormValid: false,
      newDeviceDialog: false,
      newDeviceName: '',
      newDeviceSelectedProductDevice: {
        productIndex: null,
        deviceIndex: null
      },
      loadingNewDevice: false,
      loading: false,
      snackbar: {
        show: false,
        timeout: 3000,
        color: 'error',
        text: ''
      },
      addUserDialog: false,
      loadingInviteUsers: false,
      newDevicesItemsList: {},
      newFieldsItemsList: {},
      warningDialog: {
        show: false
      },
    }
  },
  created () {
    this.monitoringProductsExpanded = this.monitoringProducts.map(() => false)
    this.extensionProductsExpanded = this.extensionProducts.map(() => false)

    this.monitoringProductsData = this.monitoringProducts.filter(product => product.type === PRODUCT_TYPES.MONITORING).map((product) => {
      const productDevices = []
      for (let i = 0; i < product.quantity; i++) {
        productDevices.push({
          device: _get(product.devices[i], 'device', null),
          deviceName: _get(product.devices[i], 'deviceName', ''),
          startDate: _get(product.devices[i], 'startDate', null),
          endDate: _get(product.devices[i], 'endDate', null),
          field: _get(product.devices[i], 'field', null),
          coordinates: _get(product.devices[i], 'coordinates', ''),
          isNewDevice: _get(product.devices[i], 'isNewDevice', false),
          startDateMenu: false,
          auxStartDate: null,
          endDateMenu: false,
          auxEndDate: null
        })
        if (_get(product.devices[i], 'isNewDevice', false)) {
          this.newDevicesItemsList[product.devices[i].device] = {
            name: product.devices[i].deviceName
          }
        }
      }
      return {
        ...product,
        devices: productDevices
      }
    })
    this.oldMonitoringProductsData = _cloneDeep(this.monitoringProductsData)

    this.extensionProductsData = this.extensionProducts.map((product) => {
      const productExtensions = []
      if (!product.extensions || product.extensions.length === 0) {
        productExtensions.push({
          device: null,
          endDateMenu: false,
          auxEndDate: null,
          endDate: null,
          unitsToExtend: 1,
          applied: false
        })
      } else {
        productExtensions.push(...product.extensions)
      }
      return {
        ...product,
        extensions: productExtensions
      }
    })
    this.oldExtensionProductsData = _cloneDeep(this.extensionProductsData)

    if (this.comesFromAnotherRoute && this.comesFromAnotherRouteData.newFieldId) {
      this.monitoringProductsData[this.comesFromAnotherRouteData.productIndex].devices[this.comesFromAnotherRouteData.deviceIndex].field = this.comesFromAnotherRouteData.newFieldId
      this.handleDataChange()
    }

    // Validate start dates on component creation
    this.$nextTick(() => {
      this.validateStartDatesOnLoad()
    })
  },
  methods: {
    async inviteUsers () {
      trackEvent(EVENTS_TO_TRACK.CLICK_CAMPAIGN_ONBOARDING_INVITE_USERS_BUTTON, {
        campaignId: this.campaignId
      })
      // Always open the dialog so editors list (and copy invitation) remains accessible
      this.addUserDialog = true
    },
    getProductDetailText (product) {
      if (product.type === PRODUCT_TYPES.MONITORING) {
        let deviceType = ''
        switch (product.deviceType) {
          case EQUIPMENT_TYPE.PIVOT:
            if (product.quantity > 1) {
              deviceType = this.$t('NewCampaignInitialData.pivots')
            } else {
              deviceType = this.$t('NewCampaignInitialData.pivot')
            }
            break
          case EQUIPMENT_TYPE.AF:
            if (product.quantity > 1) {
              deviceType = this.$t('NewCampaignInitialData.linears')
            } else {
              deviceType = this.$t('NewCampaignInitialData.linear')
            }
            break
          case EQUIPMENT_TYPE.IR:
            if (product.quantity > 1) {
              deviceType = this.$t('NewCampaignInitialData.irrigationReels')
            } else {
              deviceType = this.$t('NewCampaignInitialData.irrigationReel')
            }
            break
        }

        return this.$t('NewCampaignInitialData.introduction.contractedEquipmentsDetail', {deviceType, serviceName: product.serviceName.toUpperCase(), durationMonths: product.durationMonths})
      } else if (product.type === PRODUCT_TYPES.EXTENSION) {
        let durationMonths = product.durationMonths > 1 ? `${product.durationMonths} ${this.$t('NewCampaignInitialData.introduction.months')}` : `${product.durationMonths} ${this.$t('NewCampaignInitialData.introduction.month')}`
        if (product.quantity > 1) {
          return this.$t('NewCampaignInitialData.introduction.contractedExtensionsDetail', {durationMonths})
        } else {
          return this.$t('NewCampaignInitialData.introduction.contractedExtensionDetail', {durationMonths})
        }
      }
    },
    toggleProductExpanded (type, index) {
      if (type === PRODUCT_TYPES.MONITORING) {
        this.$set(this.monitoringProductsExpanded, index, !this.monitoringProductsExpanded[index])
      } else {
        this.$set(this.extensionProductsExpanded, index, !this.extensionProductsExpanded[index])
      }
    },
    addNewDevice (productIndex, deviceIndex) {
      trackEvent(EVENTS_TO_TRACK.CLICK_CAMPAIGN_ONBOARDING_ADD_NEW_DEVICE_BUTTON, {
        campaignId: this.campaignId
      })
      this.newDeviceSelectedProductDevice = {
        productIndex,
        deviceIndex
      }
      this.newDeviceDialog = true
    },
    handleSelectDevice (productIndex, deviceIndex) {
      this.monitoringProductsData[productIndex].devices[deviceIndex].isNewDevice = false
      this.newDevicesItemsList[`${productIndex}-${deviceIndex}`] = null
      this.checkExtensionsDevices()
      this.handleDataChange()
    },
    handleDeviceDateChange (val, device, field) {
      this.formatDeviceDate(val, device, field)
      this.handleDataChange()
    },
    formatDeviceDate (val, device, field) {
      const [year, month, day] = val.split('-')
      const formattedDate = `${day}/${month}/${year}`
      device[field] = formattedDate
      device[`${field}Menu`] = false
    },
    handleExtensionDateChange (val, extensionInfo, field) {
      this.formatExtensionDate(val, extensionInfo, field)

      // Update end date in monitoring products data
      const productIndex = this.monitoringProductsData.findIndex(product => product.devices.findIndex(device => device.device === extensionInfo.device) !== -1)
      const deviceIndex = this.monitoringProductsData[productIndex].devices.findIndex(device => device.device === extensionInfo.device)
      this.monitoringProductsData[productIndex].devices[deviceIndex][field] = extensionInfo[field]
      this.handleDataChange()
    },
    formatExtensionDate (val, extensionInfo, field) {
      const [year, month, day] = val.split('-')
      const formattedDate = `${day}/${month}/${year}`
      extensionInfo[field] = formattedDate
      extensionInfo[`${field}Menu`] = false
    },
    validateEndDateForDevice (endDate, deviceInfo, productInfo) {
      if (!endDate || !deviceInfo.startDate) return true
      const [startDay, startMonth, startYear] = deviceInfo.startDate.split('/')
      const [endDay, endMonth, endYear] = endDate.split('/')
      const startDate = new Date(startYear, startMonth - 1, startDay)
      const endDateObj = new Date(endYear, endMonth - 1, endDay)

      // Validate that end date is after start date
      if (endDateObj <= startDate) {
        return this.$t('Config.endDateAfterStartDateRule')
      }

      // Get service duration in months
      const product = productInfo

      // Get extensions assigned to the device
      const extensions = this.extensionProductsData.flatMap(product => product.extensions).filter(extension => extension.device === deviceInfo.device)
      const extensionsMonths = extensions.reduce((acc, extension) => acc + extension.unitsToExtend * this.extensionProductsData.find(product => product.extensions.find(extension => extension.device === deviceInfo.device)).durationMonths, 0)

      // Get max end date
      const maxEndDate = new Date(startDate)
      maxEndDate.setMonth(maxEndDate.getMonth() + product.durationMonths + extensionsMonths)
      return endDateObj <= maxEndDate || this.$t('Config.endDateMaxDurationRule')
    },
    validateEndDateForExtension (endDate, extensionInfo, productInfo) {
      if (!endDate && !extensionInfo.device) {
        return true
      } else if (!endDate && extensionInfo.device) {
        return this.$t('Config.fieldIsRequired')
      }
      const [startDay, startMonth, startYear] = this.monitoringProductsData.find(product => product.devices.find(device => device.device === extensionInfo.device)).devices.find(device => device.device === extensionInfo.device).startDate.split('/')
      const [endDay, endMonth, endYear] = endDate.split('/')
      const startDate = new Date(startYear, startMonth - 1, startDay)
      const endDateObj = new Date(endYear, endMonth - 1, endDay)

      // Validate that end date is after start date
      if (endDateObj <= startDate) {
        return this.$t('Config.endDateAfterStartDateRule')
      }

      // Get service duration in months
      const product = this.monitoringProductsData.find(product => product.devices.find(device => device.device === extensionInfo.device))
      const productBaseDurationMonths = product.durationMonths
      const productExtensionsMonths = extensionInfo.unitsToExtend * productInfo.durationMonths
      const maxEndDate = new Date(startDate)
      maxEndDate.setMonth(maxEndDate.getMonth() + productBaseDurationMonths + productExtensionsMonths)

      // Validate that end date is before the max end date
      return endDateObj <= maxEndDate || this.$t('Config.endDateMaxDurationRule')
    },
    validateExtensionDevice (extensionInfo) {
      if (!extensionInfo.device && extensionInfo.endDate) {
        return this.$t('Config.fieldIsRequired')
      }
      return true
    },
    getCurrentDate () {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    async finish () {
      trackEvent(EVENTS_TO_TRACK.CLICK_CAMPAIGN_ONBOARDING_DEVICES_AND_SERVICES_FINISH_BUTTON, {
        campaignId: this.campaignId
      })
      if (this.$refs.productsForm.validate()) {
        try {
          this.loading = true

          // Load campaign data querying the webhook
          const webhookUrl = `${process.env.WEBHOOKS_URL}${WEBHOOKS_PATHS.LOAD_CAMPAIGN_DATA}`
          const webhookData = {
            campaignId: this.campaignId,
            isNewCampaign: true
          }
          const response = await fetch(webhookUrl, {
            method: 'POST',
            body: JSON.stringify(webhookData),
            headers: {
              authorization: process.env.WEBHOOKS_BASIC_TOKEN,
              'Content-Type': 'application/json',
              'auth-token': await auth.currentUser.getIdToken()
            }
          })
          if (!response.ok) {
            throw new Error('Failed to load campaign data')
          }

          await completePreDataStep(this.campaignId, FIRESTORE_CAMPAIGN_PRE_DATA_STEPS.DEVICES, {campaignName: this.campaignName})
          this.nextStep()
        } catch (e) {
          console.log(e)
        } finally {
          this.loading = false
        }
      }
    },
    getDevices (deviceId, deviceType) {
      const devices = this.$store.getters.organizationDevices

      // Get all selected device IDs
      const selectedDeviceIds = []
      this.monitoringProductsData.forEach(product => {
        product.devices.forEach(deviceInfo => {
          if (deviceInfo.device && deviceInfo.device !== deviceId) {
            selectedDeviceIds.push(deviceInfo.device)
          }
        })
      })

      // Get all cloned device IDs from all devices
      const clonedDeviceIds = []
      devices.forEach(device => {
        if (device.clonedFrom && Array.isArray(device.clonedFrom)) {
          clonedDeviceIds.push(...device.clonedFrom)
        }
      })

      // Filter devices that are not selected, not cloned, and match device type
      const availableDevices = devices.filter(device =>
        !selectedDeviceIds.includes(device.id) &&
        !clonedDeviceIds.includes(device.id) &&
        device.equipment.type === deviceType
      )
      return _sortBy(availableDevices, device => device.campaign.entityName.toLowerCase())
    },
    getExtensionDevices (deviceId) {
      // Return empty array if no extension products exist
      if (!this.extensionProductsData || this.extensionProductsData.length === 0) {
        return []
      }

      // Extract all device IDs that are already selected in extensions (excluding current device)
      const selectedDevicesIds = this.extensionProductsData
        .flatMap(product => product.extensions)
        .filter(extension => extension.device && extension.device !== deviceId)
        .map(extension => extension.device)

      // Get all available devices from monitoring products
      const availableDevices = this.monitoringProductsData
        .flatMap((product, productIndex) =>
          product.devices.map((device, deviceIndex) => {
            // Try to find existing device in organization, fallback to new device template
            const existingDevice = this.$store.getters.organizationDevices.find(d => d.id === device.device)

            if (existingDevice) {
              return existingDevice
            }

            if (!device.device) {
              return
            }

            // Create new device template for devices not yet in organization
            const newDeviceId = `${productIndex}-${deviceIndex}`
            return {
              id: newDeviceId,
              campaign: { entityName: this.newDevicesItemsList[newDeviceId].name }
            }
          })
        )
        .filter(device => device && !selectedDevicesIds.includes(device.id))

      // Return devices sorted by entity name (case-insensitive)
      return _sortBy(availableDevices, device => device.campaign.entityName.toLowerCase())
    },
    async handleDataChange () {
      const auxOldMonitoring = this.oldMonitoringProductsData
      this.oldMonitoringProductsData = _cloneDeep(this.monitoringProductsData)
      const auxOldExtension = this.oldExtensionProductsData
      this.oldExtensionProductsData = _cloneDeep(this.extensionProductsData)

      if (this.saveMethods) {
        await this.saveMethods.performSave(async () => {
          await updatePreDataInformation(
            this.campaignId,
            [...auxOldMonitoring, ...auxOldExtension],
            [...this.monitoringProductsData, ...this.extensionProductsData]
          )
        })
      } else {
        await updatePreDataInformation(
          this.campaignId,
          [...auxOldMonitoring, ...auxOldExtension],
          [...this.monitoringProductsData, ...this.extensionProductsData]
        )
      }
    },
    async createNewDevice () {
      trackEvent(EVENTS_TO_TRACK.CLICK_CAMPAIGN_ONBOARDING_CREATE_NEW_DEVICE_BUTTON, {
        campaignId: this.campaignId,
        deviceName: this.newDeviceName
      })
      try {
        this.loadingNewDevice = true
        this.monitoringProductsData[this.newDeviceSelectedProductDevice.productIndex].devices[this.newDeviceSelectedProductDevice.deviceIndex].device = `${this.newDeviceSelectedProductDevice.productIndex}-${this.newDeviceSelectedProductDevice.deviceIndex}`
        this.monitoringProductsData[this.newDeviceSelectedProductDevice.productIndex].devices[this.newDeviceSelectedProductDevice.deviceIndex].deviceName = this.newDeviceName
        this.monitoringProductsData[this.newDeviceSelectedProductDevice.productIndex].devices[this.newDeviceSelectedProductDevice.deviceIndex].isNewDevice = true
        this.newDevicesItemsList[`${this.newDeviceSelectedProductDevice.productIndex}-${this.newDeviceSelectedProductDevice.deviceIndex}`] = {name: this.newDeviceName}
        this.newDeviceName = ''
        this.newDeviceSelectedProductDevice = {
          productIndex: null,
          deviceIndex: null
        }
        this.newDeviceDialog = false
        this.checkExtensionsDevices()
        this.handleDataChange()
      } catch (error) {
        this.snackbar = {
          ...this.snackbar,
          show: true,
          text: this.$t('NewCampaignInitialData.devicesAndServices.newDeviceError')
        }
      } finally {
        this.loadingNewDevice = false
      }
    },
    addNewField (productIndex, deviceIndex) {
      trackEvent(EVENTS_TO_TRACK.CLICK_CAMPAIGN_ONBOARDING_ADD_NEW_FIELD_BUTTON, {
        campaignId: this.campaignId
      })
      this.$router.push({name: 'AddField', params: {isNewField: true, comesFromAnotherRoute: true, comesFromAnotherRouteName: 'NewCampaignInitialData', comesFromAnotherRouteData: {
        productIndex,
        deviceIndex,
        campaignId: this.campaignId,
        step: CAMPAIGN_STEPS.EQUIPMENTS
      }}})
    },
    goBack () {
      trackEvent(EVENTS_TO_TRACK.CLICK_CAMPAIGN_ONBOARDING_BACK_BUTTON, {
        campaignId: this.campaignId,
        from: 'NewCampaignDevicesAndServices'
      })
      this.previousStep()
    },
    closeWarningDialog () {
      trackEvent(EVENTS_TO_TRACK.CLICK_START_DATE_WARNING_PRELOAD_BUTTON, {
        campaignId: this.campaignId,
        userId: this.$store.getters.user.id
      })
      this.warningDialog.show = false
    },
    validateStartDatesOnLoad () {
      let hasInvalidDates = false

      this.monitoringProductsData.forEach((product) => {
        product.devices.forEach((deviceInfo) => {
          if (deviceInfo.startDate) {
            const isValid = this.rules.validateStartDate(deviceInfo.startDate)
            if (isValid !== true) {
              hasInvalidDates = true
            }
          }
        })
      })

      if (hasInvalidDates) {
        this.showWarningDialog()
      }
    },
    showWarningDialog () {
      this.warningDialog.show = true
    },
    deviceHasExtension (productIndex, deviceIndex) {
      return this.extensionProductsData.flatMap(product => product.extensions).some(extension => extension.device === this.monitoringProductsData[productIndex].devices[deviceIndex].device)
    },
    validateUnitsToExtend (productIndex) {
      const sumOfUnitsToExtend = this.extensionProductsData.flatMap(product => product.extensions).reduce((acc, extension) => acc + extension.unitsToExtend, 0)
      return sumOfUnitsToExtend <= this.extensionProducts[productIndex].quantity || this.$t('Config.unitsToExtendMaxRule')
    },
    hasUnassignedExtensions (index) {
      return this.extensionProductsData[index].extensions.reduce((acc, extension) => acc + extension.unitsToExtend, 0) < this.extensionProducts[index].quantity
    },
    addNewExtension (index) {
      trackEvent(EVENTS_TO_TRACK.CLICK_CAMPAIGN_ONBOARDING_ADD_NEW_EXTENSION_BUTTON, {
        campaignId: this.campaignId
      })
      this.extensionProductsData[index].extensions.push({
        device: null,
        unitsToExtend: 1,
        endDateMenu: false,
        auxEndDate: null,
        endDate: null,
        applied: false
      })
      this.handleDataChange()
    },
    removeExtension (index, extensionIndex) {
      trackEvent(EVENTS_TO_TRACK.CLICK_CAMPAIGN_ONBOARDING_REMOVE_EXTENSION_BUTTON, {
        campaignId: this.campaignId
      })
      if (this.extensionProductsData[index].extensions.length === 1) {
        this.extensionProductsData[index].extensions[0].device = null
        this.extensionProductsData[index].extensions[0].endDate = null
        this.extensionProductsData[index].extensions[0].auxEndDate = null
      } else {
        this.extensionProductsData[index].extensions.splice(extensionIndex, 1)
      }
      this.handleDataChange()
    },
    checkExtensionsDevices () {
      // Skip if no extension products exist
      if (!this.extensionProductsData || this.extensionProductsData.length === 0) {
        return
      }

      const availableDevices = this.getExtensionDevices()
      this.extensionProductsData.forEach(product => {
        product.extensions.forEach(extension => {
          if (extension.device && !availableDevices.some(device => device.id === extension.device)) {
            extension.device = null
            extension.endDate = null
            extension.auxEndDate = null
          }
        })
      })
    }
  },
  computed: {
    fields () {
      const fields = this.$store.getters.organizationFields

      // Get all cloned field IDs from all fields
      const clonedFieldIds = []
      fields.forEach(field => {
        if (field.clonedFrom && Array.isArray(field.clonedFrom)) {
          clonedFieldIds.push(...field.clonedFrom)
        }
      })

      // Filter fields that are not cloned and return sorted
      const availableFields = fields.filter(field =>
        !clonedFieldIds.includes(field.id)
      )

      return _sortBy(availableFields, field => field.name.toLowerCase())
    },
    monitoringProducts () {
      return this.products.filter(product => product.type === PRODUCT_TYPES.MONITORING)
    },
    extensionProducts () {
      return this.products.filter(product => product.type === PRODUCT_TYPES.EXTENSION)
    }
  }
}
</script>



// WEBPACK FOOTER //
// NewCampaignDevicesAndServices.vue?71455f94